import React, { useState, useRef, useEffect } from 'react';
import InviteFieldInline from './InviteFieldInline';
import { createAccessRequest } from '../api/feedback';

interface InviteRequestFormProps {
  onBack: () => void;
}

const InviteRequestForm: React.FC<InviteRequestFormProps> = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async () => {
    if (!email.trim()) {
      setStatus('error');
      setMessage('Please enter your email address.');
      return;
    }

    if (!validateEmail(email)) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }

    setStatus('loading');
    
    try {
      await createAccessRequest({
        email: email.trim().toLowerCase()
      });
      setStatus('success');
      setMessage('Request submitted! We\'ll review and send you beta access shortly.');
    } catch (error) {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div style={{
            height: '40px',
            width: '40px',
            borderRadius: '12px',
            background: 'rgba(0,128,96,0.25)',
            border: '1px solid rgba(100,255,220,0.35)',
            display: 'grid',
            placeItems: 'center',
            marginRight: '12px'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17l-5-5" stroke="#64ffdc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <h2 style={{
              fontSize: '22px',
              fontWeight: 700,
              letterSpacing: '-0.01em',
              margin: 0,
              color: '#e7f5f1'
            }}>
              Request Submitted
            </h2>
            <div style={{
              color: '#b8d3cb',
              fontSize: '14px',
              marginTop: '2px'
            }}>
              Check your email for the invite code
            </div>
          </div>
        </div>

        <div style={{
          padding: '16px',
          borderRadius: '12px',
          background: 'rgba(0,128,96,0.25)',
          border: '1px solid rgba(100,255,220,0.35)',
          color: '#d1fff4',
          fontSize: '14px',
          marginBottom: '16px'
        }}>
          {message}
        </div>

        <button
          onClick={onBack}
          style={{
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.24)',
            color: '#b8d3cb',
            padding: '12px 16px',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '14px',
            width: '100%'
          }}
        >
          ← Back to invite code
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <div style={{
          height: '40px',
          width: '40px',
          borderRadius: '12px',
          background: 'rgba(28,182,154,0.18)',
          border: '1px solid rgba(28,182,154,0.32)',
          display: 'grid',
          placeItems: 'center',
          marginRight: '12px'
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="rgba(28,182,154,0.9)" strokeWidth="2"/>
            <polyline points="22,6 12,13 2,6" stroke="rgba(28,182,154,0.9)" strokeWidth="2"/>
          </svg>
        </div>
        <div>
          <h2 style={{
            fontSize: '22px',
            fontWeight: 700,
            letterSpacing: '-0.01em',
            margin: 0,
            color: '#e7f5f1'
          }}>
            Request beta access
          </h2>
          <div style={{
            color: '#b8d3cb',
            fontSize: '14px',
            marginTop: '2px'
          }}>
            We'll review our current user count and sent an invite code shortly
          </div>
        </div>
      </div>

      <label style={{
        marginBottom: '8px',
        display: 'block',
        fontSize: '13px',
        color: '#d9f3ec'
      }}>
        Your email address
      </label>

      <InviteFieldInline
        ref={inputRef}
        value={email}
        onChange={setEmail}
        onSubmit={handleSubmit}
        placeholder="your@email.com"
        disabled={status === 'loading'}
        buttonText={status === 'loading' ? 'Sending...' : 'Request Code'}
      />

      <div style={{
        marginTop: '10px',
        color: '#8fa7a1',
        fontSize: '13px'
      }}>
        We'll only use your email to send you beta access.
      </div>

      {status === 'error' && (
        <div style={{
          marginTop: '12px',
          padding: '10px 12px',
          borderRadius: '12px',
          fontSize: '14px',
          color: '#ffd7d7',
          background: 'rgba(128,0,0,0.25)',
          border: '1px solid rgba(255,130,130,0.35)'
        }}>
          {message}
        </div>
      )}

      <div style={{
        marginTop: '16px',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#b8d3cb',
            textDecoration: 'underline',
            textDecorationColor: 'rgba(255,255,255,0.24)',
            cursor: 'pointer',
            fontSize: '13px'
          }}
        >
          ← Back to invite code
        </button>
      </div>
    </div>
  );
};

export default InviteRequestForm;